import React, { useEffect, useState } from 'react';
import { Card, Button, Input, Modal, Tag, Alert } from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  FormOutlined,
  CheckCircleFilled,
  SendOutlined,
  FileDoneOutlined,
} from '@ant-design/icons';
import { MarkdownContent } from './MarkdownContent.jsx';
import { lessonApi } from '../../../api/lesson.api.js';

const MAX_LENGTH = 10000; // khớp submitAssignmentSchema ở máy chủ

export function AssignmentBox({ lessonId, assignment, lessonTitle }) {
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [submissionText, setSubmissionText] = useState('');
  const [submission, setSubmission] = useState(null); // bài đã nộp lưu trên máy chủ (HT-06 F-04)
  const [submitting, setSubmitting] = useState(false);

  const hasAssignment = !!assignment?.content;

  // Tải bài đã nộp để hiện trạng thái và điền sẵn khi nộp lại
  useEffect(() => {
    if (!lessonId || !hasAssignment) return undefined;
    let cancelled = false;
    lessonApi
      .getMyAssignmentSubmission(lessonId)
      .then((res) => {
        if (cancelled || !res.data) return;
        setSubmission(res.data);
        setSubmissionText(res.data.content || '');
      })
      .catch(() => {}); // không tải được thì vẫn cho nộp, chỉ thiếu trạng thái cũ
    return () => {
      cancelled = true;
    };
  }, [lessonId, hasAssignment]);

  if (!hasAssignment) {
    return null;
  }

  const handleSubmit = async () => {
    if (!submissionText.trim()) {
      toast.warning('Vui lòng nhập nội dung bài thực hành của bạn!');
      return;
    }
    setSubmitting(true);
    try {
      const res = await lessonApi.submitAssignment(lessonId, submissionText.trim());
      setSubmission(res.data);
      setShowSubmitModal(false);
      toast.success(submission ? 'Đã cập nhật bài thực hành!' : 'Đã nộp bài thực hành thành công!');
    } catch (err) {
      toast.error(err.message || 'Không nộp được bài, vui lòng thử lại');
    } finally {
      setSubmitting(false);
    }
  };

  const submittedAt = submission ? new Date(submission.updatedAt).toLocaleString('vi-VN') : null;

  return (
    <Card
      className="lesson-assignment"
      style={{
        borderRadius: 12,
        border: '1px solid #fed7aa',
        background: '#fffaf5',
        marginBottom: 32,
      }}
      styles={{ body: { padding: '24px 28px' } }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FileDoneOutlined style={{ fontSize: 22, color: '#ea580c' }} />
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 700, color: '#9a3412', margin: 0 }}>
              {assignment.title || 'Bài Tập Thực Hành & Áp Dụng Tại Quầy'}
            </h3>
            <span style={{ fontSize: 12, color: '#c2410c' }}>
              Thực hành trực tiếp trên dữ liệu nhà thuốc của bạn để khắc sâu kiến thức
            </span>
          </div>
        </div>

        {submission ? (
          <Tag color="success" icon={<CheckCircleFilled />} title={`Nộp lúc ${submittedAt}`}>
            Đã nộp bài
          </Tag>
        ) : (
          <Tag color="orange">Yêu cầu hoàn thành</Tag>
        )}
      </div>

      <div
        className="lesson-assignment-body"
        style={{
          background: '#ffffff',
          borderRadius: 8,
          padding: '16px 20px',
          border: '1px solid #ffedd5',
          marginBottom: 16,
        }}
      >
        <MarkdownContent content={assignment.content} isPractice={true} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {submission && (
          <span style={{ fontSize: 12, color: '#9a3412' }}>
            Nộp lần {submission.submitCount} lúc {submittedAt}
          </span>
        )}
        <Button
          type="primary"
          icon={<FormOutlined />}
          onClick={() => setShowSubmitModal(true)}
          style={{ background: '#ea580c', borderColor: '#ea580c' }}
        >
          {submission ? 'Cập nhật bài nộp' : 'Nộp bài thực hành'}
        </Button>
      </div>

      <Modal
        title={`Nộp Bài Thực Hành: ${lessonTitle || ''}`}
        open={showSubmitModal}
        onCancel={() => setShowSubmitModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowSubmitModal(false)}>
            Hủy
          </Button>,
          <Button
            key="submit"
            type="primary"
            icon={<SendOutlined />}
            loading={submitting}
            onClick={handleSubmit}
            style={{ background: '#ea580c', borderColor: '#ea580c' }}
          >
            Xác Nhận Nộp Bài
          </Button>,
        ]}
      >
        <Alert
          message="Hướng dẫn nộp bài"
          description="Dán kết quả tính toán, số liệu P&L, liên kết Google Drive, hoặc bản thảo nội dung thực hành của bạn vào ô dưới đây. Nộp lại sẽ thay bài cũ."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Input.TextArea
          rows={6}
          maxLength={MAX_LENGTH}
          showCount
          placeholder="Nhập nội dung bài thực hành của bạn tại đây..."
          value={submissionText}
          onChange={(e) => setSubmissionText(e.target.value)}
        />
      </Modal>
    </Card>
  );
}
